import { useEffect, useState } from 'react';

import { isAndroid } from '../mobile/deviceDetection';

// The SDK's LatLng type allows `number | string`, so coerce before checking —
// but an empty string / null must NOT coerce to 0 via Number('').
const isFiniteCoord = (v) => v !== '' && v != null && Number.isFinite(Number(v));
const hasCoords = (t) => t && isFiniteCoord(t.lat) && isFiniteCoord(t.lng);
// Escape parentheses left unescaped by encodeURIComponent — they would
// otherwise terminate the geo: URI's `q=lat,lng(label)` group early.
const escapeLabel = (label) =>
  encodeURIComponent(label).replace(/\(/g, '%28').replace(/\)/g, '%29');

/**
 * Wegbeschreibungs-Link zu einem Ziel.
 * - Standard: Google-Maps-Directions (https) — Desktop, iOS, SSR.
 * - Android: `geo:`-URI, geht direkt an die Karten-App statt an den Browser
 *   (Firefox-„App öffnen"-Dialog scheitert auf Xiaomi/Samsung; Spec 2026-09-08).
 *
 * @param {{ lat?: number, lng?: number, address?: string } | null | undefined} target
 * @param {{ android?: boolean, label?: string }} [opts]
 * @returns {string | null}
 */
export const buildDirectionsUrl = (target, { android = false, label } = {}) => {
  if (!target) return null;
  if (hasCoords(target)) {
    const c = `${Number(target.lat)},${Number(target.lng)}`;
    if (!android) return `https://www.google.com/maps/dir/?api=1&destination=${c}`;
    const q = label ? `${c}(${escapeLabel(label)})` : c;
    return `geo:${c}?q=${q}`;
  }
  if (target.address) {
    const q = encodeURIComponent(target.address);
    return android ? `geo:0,0?q=${q}` : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
  }
  return null;
};

/** Cordova-App auf Android ODER Android-Browser. Auf dem Server immer false. */
export const isAndroidDevice = () =>
  isAndroid() || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));

/**
 * Hydration-sicher: erster Render (= SSR-Markup) liefert den https-Link,
 * nach Mount auf Android den geo:-Link.
 *
 * @param {{ lat?: number, lng?: number, address?: string } | null | undefined} target
 * @param {string} [label]
 * @returns {string | null}
 */
export const useDirectionsUrl = (target, label) => {
  const [android, setAndroid] = useState(false);
  useEffect(() => {
    setAndroid(isAndroidDevice());
  }, []);
  return buildDirectionsUrl(target, { android, label });
};
